import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import SafeHtml from "../components/SafeHtml";
import { toast } from "sonner";
import PageAdvice from "../components/PageAdvice";
import {
  ArrowLeft, CircleNotch, WarningCircle, FileText, CurrencyDollar, Receipt,
  Wrench, Note, Paperclip, MapPin, User, CalendarBlank, LinkSimple, Flag,
  ArrowsClockwise, Database,
} from "@phosphor-icons/react";

const fmtSync = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : "never");

const STATUS_COLORS = {
  PENDING: "#0055FF", ACCEPTED: "#059669", REJECTED: "#DC2626",
  DRAFT: "#6B7280", EXPIRED: "#D97706", INVOICED: "#7C3AED",
};
const sc = (s) => STATUS_COLORS[(s || "").toUpperCase()] || "#6B7280";
const money = (n) => (n == null ? "—" : "$" + Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm">
      <div className="flex items-center gap-2 text-neutral-500"><Icon size={16} weight="bold" /><span className="text-xs uppercase tracking-widest font-semibold">{label}</span></div>
      <p className={`mt-2 font-heading text-2xl font-black tracking-tighter ${accent || "text-[#0A0A0A]"}`}>{value}</p>
    </div>
  );
}
function Row({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex items-start justify-between gap-4 border-b border-neutral-100 py-2.5 last:border-0">
      <span className="text-sm text-neutral-500">{label}</span>
      <span className="text-sm font-semibold text-[#0A0A0A] text-right">{value}</span>
    </div>
  );
}

export default function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/quote/${id}`).then((r) => setD(r.data)).catch((e) => setError(e.response?.data?.detail || e.message)).finally(() => setLoading(false));
  }, [id]);

  const refresh = () => {
    setRefreshing(true);
    toast.promise(
      api.get(`/quote/${id}`, { params: { refresh: true } }).then((r) => setD(r.data)).finally(() => setRefreshing(false)),
      { loading: "Pulling latest from Aroflo…", success: "Quote refreshed from Aroflo.", error: "Refresh failed." }
    );
  };

  if (loading) return <Layout><div className="flex h-64 items-center justify-center" data-testid="quote-loading"><CircleNotch size={28} className="animate-spin text-[#0055FF]" /></div></Layout>;
  if (error) return <Layout><div className="flex h-64 flex-col items-center justify-center gap-2 text-neutral-600" data-testid="quote-error"><WarningCircle size={28} className="text-[#D97706]" />{error}</div></Layout>;

  const c = d.counts || {};
  const client = d.client || {};
  const incTax = c.taxInclusive;

  return (
    <Layout>
      <div data-testid="quote-detail-page">
        <button data-testid="quote-back" onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1.5 text-sm font-semibold text-neutral-600 hover:text-[#0055FF]"><ArrowLeft size={16} weight="bold" /> Back</button>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-sm bg-[#0A0A0A] text-white"><FileText size={24} weight="fill" /></div>
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-[#0A0A0A]" data-testid="quote-number">Quote · Job #{d.jobNumber || d.id}</h1>
              <p className="text-sm text-neutral-500">{d.taskType?.name || "Quote"} · {client.id ? <button onClick={() => navigate(`/clients/${client.id}`)} className="font-semibold text-[#0055FF] hover:underline">{client.name}</button> : "—"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded-sm px-3 py-1.5 text-sm font-semibold" style={{ background: sc(d.status) + "1a", color: sc(d.status) }} data-testid="quote-status">{d.status}</span>
            <button
              data-testid="quote-refresh-btn"
              onClick={refresh}
              disabled={refreshing}
              className="flex items-center gap-2 rounded-sm border border-[#0055FF] px-3.5 py-1.5 text-sm font-semibold text-[#0055FF] transition-all duration-200 hover:bg-[#0055FF]/5 disabled:opacity-50"
            >
              <ArrowsClockwise size={15} weight="bold" className={refreshing ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        </div>
        <p className="mt-2 flex items-center gap-1.5 text-xs text-neutral-400" data-testid="quote-synced-at">
          <Database size={12} /> {d._source === "db" ? "From cache · " : "Synced · "}{fmtSync(d._cached_at)}
        </p>

        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Stat icon={CurrencyDollar} label={`Total ${incTax ? "(inc)" : "(ex)"}`} value={money(c.total)} accent="text-[#0055FF]" />
          <Stat icon={Receipt} label="Tax" value={money(c.tax)} />
          <Stat icon={CurrencyDollar} label="Materials" value={money(c.materials)} />
          <Stat icon={Wrench} label="Labour" value={money(c.labour)} />
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm">
            <h3 className="mb-2 font-heading text-lg font-black tracking-tight">Quote Details</h3>
            <Row label="Reference" value={d.referenceNumber} />
            <Row label="Status" value={d.status} />
            <Row label="Acceptance" value={d.acceptanceStatus} />
            <Row label="Due Date" value={d.dueDate} />
            <Row label="Business Unit" value={d.owner?.name} />
            <Row label="Entered By" value={d.enteredBy} />
            <Row label="Estimated By" value={d.estimatedByUser ? `${d.estimatedByUser.givenName || ""} ${d.estimatedByUser.familyName || ""}`.trim() : null} />
            <Row label="Priority" value={d.priority?.description} />
          </div>

          <div className="lg:col-span-2 space-y-6">
            <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm">
              <h3 className="mb-2 flex items-center gap-2 font-heading text-lg font-black tracking-tight"><MapPin size={18} /> Location</h3>
              <p className="text-sm text-neutral-700">{d.locationText || "—"}</p>
            </div>

            {d.task?.referenceNumber && (
              <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm" data-testid="linked-task">
                <h3 className="mb-2 flex items-center gap-2 font-heading text-lg font-black tracking-tight"><LinkSimple size={18} /> Linked Job</h3>
                {d.task.id ? (
                  <button
                    data-testid="linked-job-link"
                    onClick={() => navigate(`/jobs/${d.task.id}`)}
                    className="flex w-full items-center justify-between gap-4 rounded-sm border border-neutral-200 bg-neutral-50 px-3 py-3 text-left transition-all duration-200 hover:border-[#0055FF] hover:bg-[#0055FF]/[0.04]"
                  >
                    <div>
                      <p className="font-mono text-sm font-semibold text-[#0055FF]">{d.task.referenceNumber}</p>
                      {d.task.name && <p className="text-xs text-neutral-500">{d.task.name}</p>}
                    </div>
                    <ArrowLeft size={16} className="rotate-180 text-neutral-400" />
                  </button>
                ) : (
                  <Row label="Job Reference" value={d.task.referenceNumber} />
                )}
              </div>
            )}

            <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm">
              <h3 className="mb-2 font-heading text-lg font-black tracking-tight">Records</h3>
              <Row label="Documents" value={c.documents ?? 0} />
              <Row label="Quote Notes" value={c.quoteNotes ?? 0} />
              <Row label="Forms" value={`${c.completedForms || 0}/${c.totalForms || 0}`} />
              <Row label="Invoices" value={c.invoices ?? 0} />
              <Row label="Material Markup" value={c.materialMarkup != null ? `${c.materialMarkup}%` : null} />
            </div>

            {d.description ? (
              <div className="border border-neutral-200 bg-white p-5 shadow-sm rounded-sm">
                <h3 className="mb-3 font-heading text-lg font-black tracking-tight">Description</h3>
                <SafeHtml html={d.description} className="prose prose-sm max-w-none text-sm text-neutral-700" data-testid="quote-description" />
              </div>
            ) : null}
          </div>
        </div>
        <PageAdvice items={[
          "This quote is cached in the Hub database. Use \u201cRefresh\u201d to pull the latest figures, status and linked job from Aroflo.",
          "Totals reflect Aroflo\u2019s tax setting (inc/ex) at the time of the last sync.",
        ]} />
      </div>
    </Layout>
  );
}
